import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

/**
 * ============================================================
 * GSAP EXPERIENCE — MINI ŚCIĄGA UŻYCIA
 * ============================================================
 *
 * Ten plik działa jako prosty silnik efektów opartych o atrybuty data-* w HTML.
 * Logika animacji siedzi tutaj, ale to HTML decyduje CO ma się animować.
 *
 * ------------------------------------------------------------
 * 1. EFEKTY DLA SEKCJI / BLOKÓW — data-fx="..."
 * ------------------------------------------------------------
 * Dostępne presety:
 *
 * data-fx="section-soft"
 * - delikatny fade up + lekki scale z mniejszego do normalnego,
 * - główny preset dla zwykłych sekcji,
 * - najbezpieczniejszy i najbardziej „premium”.
 *
 * data-fx="section-soft-lg"
 * - trochę mocniejsza wersja section-soft,
 * - dobra dla większych bloków wizualnych.
 *
 * data-fx="fade-left-soft"
 * - subtelne wejście z lewej.
 *
 * data-fx="fade-right-soft"
 * - subtelne wejście z prawej.
 *
 * data-fx="zoom-in-soft"
 * - delikatny zoom-in z fade,
 * - dobry np. dla sekcji video / media.
 *
 * data-fx="none"
 * - świadome wyłączenie efektu.
 *
 * Dodatkowe opcje dla data-fx:
 * - data-fx-delay="0.15"
 * - data-fx-duration="1.1"
 * - data-fx-start="top 88%"
 * - data-fx-ease="power2.out"
 * - data-fx-once="true"
 *
 * Przykład:
 * <section data-fx="section-soft"></section>
 * <div data-fx="fade-left-soft" data-fx-delay="0.1"></div>
 *
 * ------------------------------------------------------------
 * 2. EFEKTY DLA BACKGROUNDÓW — data-fx-bg="..."
 * ------------------------------------------------------------
 * Dostępne presety:
 *
 * data-fx-bg="parallax-soft"
 * - delikatny parallax tła,
 * - idealny dla hero i CTA.
 *
 * data-fx-bg="parallax-soft-reverse"
 * - parallax w przeciwnym kierunku.
 *
 * data-fx-bg="zoom-parallax-soft"
 * - subtelny zoom + parallax,
 * - mocniej wizualny efekt, raczej używać oszczędnie.
 *
 * Dodatkowe opcje dla data-fx-bg:
 * - data-bg-from="50% 0%"
 * - data-bg-to="50% 60%"
 * - data-bg-scrub="0.9"
 *
 * Przykład:
 * <div data-fx-bg="parallax-soft" style="background-image:url(...)"></div>
 *
 * * ------------------------------------------------------------
 * 3. DEKORACJE Z PARALLAXEM — X / Y / ROTACJA (NOWOŚCI 🔥)
 * ------------------------------------------------------------
 * Użycie: [wartość DOCELOWA] lub [wartość STARTOWA, wartość DOCELOWA]
 * Jednostki: px, %, vw. Jeśli podasz samą liczbę (np. "-12"), GSAP uzna to za procenty.
 * * Przykłady osi Y:
 * data-parallax-y="-25%"        -> jedzie z 0 do -25%
 * data-parallax-y="80%, 0"      -> zaczyna na 80%, kończy na 0 (pozycja naturalna)
 * data-parallax-y="-50px, 50px" -> od -50px do 50px
 * * Przykłady osi X:
 * data-parallax-x="100px, 0"    -> wjeżdża z prawej do zera
 * * Przykład rotacji i scrubu:
 * data-parallax-r="0, 360"      -> kręci się wokół własnej osi
 * data-parallax-scrub="1.5"     -> indywidualne "opóźnienie/bezwładność" efektu
 *
 * ------------------------------------------------------------
 * 4. KONTENER ODNIESIENIA — data-parallax-section
 * ------------------------------------------------------------
 * Oznacza kontener, względem którego liczony jest zakres efektu parallax.
 * Jeśli istnieje, to on staje się triggerem ScrollTrigger.
 *
 * Przykład:
 * <section data-parallax-section>
 *   <img data-parallax-y="-12" alt="">
 * </section>
 *
 * ------------------------------------------------------------
 * 5. SPECJALNE ATRYBUTY HERO
 * ------------------------------------------------------------
 * data-hero-bg
 * data-hero-title
 * data-hero-copy
 * data-hero-cta
 *
 * Te atrybuty są zarezerwowane dla intro hero i sterują wejściem
 * pierwszego ekranu strony.
 *
 * ------------------------------------------------------------
 * 6. REKOMENDACJA UŻYCIA
 * ------------------------------------------------------------
 * - nagłówki tekstowe mogą mieć osobne animacje tekstowe,
 * - dla większości sekcji używaj: data-fx="section-soft",
 * - dla większych bloków: data-fx="section-soft-lg",
 * - dla CTA i hero: data-fx-bg="parallax-soft",
 * - nie przesadzaj z liczbą efektów na jednej sekcji.
 */

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

const MEDIA = {
    desktop: '(min-width: 992px)',
    reduced: '(prefers-reduced-motion: reduce)',
};

const FX_PRESETS = {
    'section-soft': { y: 42, scale: 0.965, opacity: 0, duration: 1.05, ease: 'power2.out', start: 'top 88%', once: true },
    'section-soft-lg': { y: 56, scale: 0.955, opacity: 0, duration: 1.15, ease: 'power2.out', start: 'top 90%', once: true },
    'fade-left-soft': { x: -36, opacity: 0, duration: 0.95, ease: 'power2.out', start: 'top 88%', once: true },
    'fade-right-soft': { x: 36, opacity: 0, duration: 0.95, ease: 'power2.out', start: 'top 88%', once: true },
    'zoom-in-soft': { scale: 0.975, opacity: 0, duration: 1, ease: 'power2.out', start: 'top 88%', once: true },
};

const BG_PRESETS = {
    'parallax-soft': { from: '50% 0%', to: '50% 65%', scrub: 0.9, desktopOnly: false },
    'parallax-soft-reverse': { from: '50% 40%', to: '50% 0%', scrub: 0.9, desktopOnly: false },
    'zoom-parallax-soft': { from: '50% 0%', to: '50% 60%', scrub: 1, scaleFrom: 1.04, scaleTo: 1, desktopOnly: true },
};

function getBool(value, fallback = false) {
    if (value == null || value === '') return fallback;
    return value === 'true' || value === '1';
}

function getNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function getFxType(element) {
    return element.dataset.fx || null;
}

function getFxConfig(element) {
    const type = getFxType(element);
    const preset = type ? FX_PRESETS[type] : null;

    if (!preset) return null;

    return {
        ...preset,
        delay: getNumber(element.dataset.fxDelay, 0),
        duration: getNumber(element.dataset.fxDuration, preset.duration),
        ease: element.dataset.fxEase || preset.ease,
        start: element.dataset.fxStart || preset.start,
        once: getBool(element.dataset.fxOnce, preset.once),
    };
}

function updateNavHeightVar(header) {
    if (!header) return;
    document.documentElement.style.setProperty('--nav-h', `${header.offsetHeight}px`);
}

function initSmoothScroll({ header, navbarCollapse }) {
    const wrapper = document.querySelector('#smooth-wrapper');
    const content = document.querySelector('#smooth-content');

    if (!wrapper || !content) return null;

    const reducedMotion = window.matchMedia(MEDIA.reduced).matches;
    const isDesktop = window.matchMedia(MEDIA.desktop).matches;

    if (reducedMotion || !isDesktop) {
        updateNavHeightVar(header);
        window.addEventListener('resize', () => updateNavHeightVar(header), { passive: true });
        window.addEventListener('load', () => updateNavHeightVar(header), { passive: true });
        return null;
    }

    const smoother = ScrollSmoother.create({
        wrapper,
        content,
        smooth: 1.65,
        smoothTouch: 0,
        normalizeScroll: true,
        ignoreMobileResize: true,
        effects: true,
    });

    updateNavHeightVar(header);
    window.addEventListener('resize', () => updateNavHeightVar(header), { passive: true });
    window.addEventListener('load', () => updateNavHeightVar(header), { passive: true });

    if (navbarCollapse) {
        const pauseSmoother = () => smoother.paused(true);
        const resumeSmoother = () => {
            smoother.paused(false);
            ScrollTrigger.refresh();
        };

        navbarCollapse.addEventListener('show.bs.collapse', pauseSmoother);
        navbarCollapse.addEventListener('shown.bs.collapse', () => updateNavHeightVar(header));
        navbarCollapse.addEventListener('hidden.bs.collapse', () => {
            updateNavHeightVar(header);
            resumeSmoother();
        });
        navbarCollapse.addEventListener('hide.bs.collapse', resumeSmoother);
    }

    return smoother;
}

function initHeaderScroll(header, navbarCollapse) {
    if (!header) return;

    ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => {
            const menuOpen = navbarCollapse?.classList.contains('show');
            if (menuOpen) {
                header.classList.remove('header-hidden');
                return;
            }

            if (self.scroll() <= 10 || self.direction === -1) {
                header.classList.remove('header-hidden');
                return;
            }

            if (self.direction === 1 && self.scroll() > 120) {
                header.classList.add('header-hidden');
            }
        },
    });
}

function initAnchorScrolling(smoother) {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    if (!anchorLinks.length) return;

    anchorLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
            const href = link.getAttribute('href');
            if (!href || href === '#') return;

            const target = document.querySelector(href);
            if (!target) return;

            event.preventDefault();

            if (smoother) {
                smoother.scrollTo(target, true, 'top top+=96');
            } else {
                gsap.to(window, {
                    duration: 1,
                    scrollTo: { y: target, offsetY: 96 },
                    ease: 'power3.out',
                });
            }
        });
    });
}

function initHeroIntro() {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    const background = hero.querySelector('[data-hero-bg]');
    const title = hero.querySelector('[data-hero-title]');
    const copy = hero.querySelector('[data-hero-copy]');
    const cta = hero.querySelector('[data-hero-cta]');

    const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

    if (background) {
        timeline.from(background, { scale: 1.12, duration: 1.6, ease: 'power2.out' });
    }

    if (title) {
        timeline.from(title, { y: 56, opacity: 0, duration: 0.9 }, '-=1.1');
    }

    if (copy) {
        timeline.from(copy, { y: 32, opacity: 0, duration: 0.7 }, '-=0.55');
    }

    if (cta) {
        timeline.from(cta, { y: 24, opacity: 0, duration: 0.6 }, '-=0.35');
    }
}

function initSectionEffects() {
    document.querySelectorAll('[data-fx]').forEach((element) => {
        const type = getFxType(element);
        if (!type || type === 'none') return;

        const config = getFxConfig(element);
        if (!config) return;

        const { start, once, ...tweenVars } = config;

        gsap.from(element, {
            ...tweenVars,
            clearProps: 'all',
            scrollTrigger: {
                trigger: element,
                start,
                once,
            },
        });
    });
}

function initBackgroundEffects() {
    const isDesktop = window.matchMedia(MEDIA.desktop).matches;

    // 1. Obsługa efektów tła (bez zmian)
    document.querySelectorAll('[data-fx-bg]').forEach((element) => {
        const type = element.dataset.fxBg;
        const preset = BG_PRESETS[type];
        if (!preset) return;
        if (preset.desktopOnly && !isDesktop) return;

        const trigger = element.closest('[data-parallax-section]') || element;
        const from = element.dataset.bgFrom || preset.from;
        const to = element.dataset.bgTo || preset.to;
        const scrub = getNumber(element.dataset.bgScrub, preset.scrub);

        const fromState = { backgroundPosition: from };
        const toState = {
            backgroundPosition: to,
            ease: 'none',
            scrollTrigger: {
                trigger,
                start: 'top bottom',
                end: 'bottom top',
                scrub,
            },
        };

        if (preset.scaleFrom) {
            fromState.scale = preset.scaleFrom;
            toState.scale = preset.scaleTo ?? 1;
            toState.transformOrigin = '50% 50%';
        }

        gsap.fromTo(element, fromState, toState);
    });

    // 2. NOWY, ULEPSZONY SILNIK PARALLAX (X, Y, Obrót, Skala, Start/Stop)
    document.querySelectorAll('[data-parallax-y], [data-parallax-x], [data-parallax-r], [data-parallax-scale]').forEach((element) => {
        if (element.classList.contains('who-we-are__image')) return;

        // NOWA LINIJKA: Wyłącza animację, jeśli element ma atrybut desktop-only, a my jesteśmy na mobile (< 992px)
        if (element.dataset.parallaxDesktopOnly === 'true' && !isDesktop) return;

        const trigger = element.closest('[data-parallax-section]') || element.parentElement || element;
        const scrubVal = getNumber(element.dataset.parallaxScrub, 0.6);

        // Dodaliśmy parametr isPositional, żeby odróżnić pozycję (X, Y) od skali i rotacji
        const parseAxis = (valStr, isPositional = true) => {
            if (!valStr) return { from: null, to: null };

            const parts = valStr.split(',').map(s => s.trim());
            // Zabezpieczenie dla skali: domyślny start to 1, nie 0 (żeby element nie znikał, jeśli podasz jedną wartość)
            const defaultStart = isPositional ? 0 : 1;
            let fromVal = parts.length === 2 ? parts[0] : defaultStart;
            let toVal = parts.length === 2 ? parts[1] : parts[0];

            const processValue = (v) => {
                const isPureNumber = !isNaN(v) && v !== '';
                if (isPureNumber) {
                    if (isPositional) {
                        // Tylko dla X i Y przeliczamy na procenty i dodajemy mnożnik mobilny
                        const num = isDesktop ? Number(v) : Number(v) * 0.35;
                        return `${num}%`;
                    } else {
                        // Dla scale i rotacji (R) zwracamy czystą liczbę
                        return Number(v);
                    }
                }
                return v;
            };

            return { from: processValue(fromVal), to: processValue(toVal) };
        };

        const yAxis = parseAxis(element.dataset.parallaxY, true);
        const xAxis = parseAxis(element.dataset.parallaxX, true);
        const rAxis = parseAxis(element.dataset.parallaxR, false); // false, bo to rotacja
        const scaleAxis = parseAxis(element.dataset.parallaxScale, false); // NOWE: false, bo to skala

        const fromState = {};
        const startVal = element.dataset.parallaxStart || 'top bottom';
        const endVal = element.dataset.parallaxEnd || 'bottom top';

        const toState = {
            ease: 'none',
            scrollTrigger: {
                trigger,
                start: startVal,
                end: endVal,
                scrub: scrubVal,
            },
        };

        // Zbieranie osi
        if (yAxis.to !== null) { fromState.y = yAxis.from; toState.y = yAxis.to; }
        if (xAxis.to !== null) { fromState.x = xAxis.from; toState.x = xAxis.to; }
        if (rAxis.to !== null) { fromState.rotation = rAxis.from; toState.rotation = rAxis.to; }
        if (scaleAxis.to !== null) { fromState.scale = scaleAxis.from; toState.scale = scaleAxis.to; } // NOWE

        gsap.fromTo(element, fromState, toState);
    });
}

function initWhoWeAreImageReveal() {
    const imageWrapper = document.querySelector('.who-we-are__image');
    const section = document.querySelector('.who-we-are');

    if (!imageWrapper || !section) return;
    const isDesktop = window.matchMedia(MEDIA.desktop).matches;

    gsap.fromTo(imageWrapper, {
        y: isDesktop ? 72 : 36,
    }, {
        y: 0,
        ease: 'none',
        scrollTrigger: {
            trigger: section,
            start: isDesktop ? 'top 105%' : 'top 92%',
            end: isDesktop ? 'top 50%' : 'top 62%',
            scrub: isDesktop ? 1.5 : 1,
        },
    });
}

function initWhyUsImageReveal() {
    const imageWrapper = document.querySelector('.why-us__image');
    const trigger = document.querySelector('.why-us [data-parallax-section]') || document.querySelector('.why-us');

    if (!imageWrapper || !trigger) return;
    const isDesktop = window.matchMedia(MEDIA.desktop).matches;

    gsap.fromTo(imageWrapper, {
        x: isDesktop ? 44 : 18,
        y: isDesktop ? 54 : 24,
        scale: isDesktop ? 1.02 : 1.01,
        opacity: 0.92,
    }, {
        x: 0,
        y: 0,
        scale: 1,
        opacity: 1,
        ease: 'none',
        scrollTrigger: {
            trigger,
            start: isDesktop ? 'top 92%' : 'top 88%',
            end: isDesktop ? 'top 48%' : 'top 60%',
            scrub: isDesktop ? 1.4 : 1,
        },
    });
}

function initReducedMotionFallback() {
    if (!window.matchMedia(MEDIA.reduced).matches) return false;

    document.documentElement.classList.add('reduced-motion');
    document.querySelectorAll('[data-fx]').forEach((element) => {
        element.style.opacity = '1';
        element.style.transform = 'none';
    });

    return true;
}

export function initGsapExperience({ header, navbarCollapse } = {}) {
    const reducedMotion = initReducedMotionFallback();

    const smoother = initSmoothScroll({ header, navbarCollapse });

    initHeaderScroll(header, navbarCollapse);
    initAnchorScrolling(smoother);

    if (!reducedMotion) {
        initHeroIntro();
        initSectionEffects();
        initBackgroundEffects();
        initWhoWeAreImageReveal();
        initWhyUsImageReveal();
    }

    window.setTimeout(() => ScrollTrigger.refresh(), 250);

    return {
        smoother,
        refresh: () => ScrollTrigger.refresh(),
    };
}